import React, { useEffect, useMemo, useRef, useState } from 'react';
import StandardAppLayout from 'src/shared/components/layout/standard-app-layout/StandardAppLayout';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Save as SaveOutlined } from 'lucide-react';

import useCustomer from 'src/app/customers/hooks/useCustomer';
import { reset } from 'src/app/projects/views/new-project-definition/state/newProjectDefinitionSlice';
import { SecondaryButton } from 'src/shared/components/button/Button';
import Confirm from 'src/shared/components/confirm/Confirm';
import WarningMessage from 'src/shared/components/warning-message/WarningMessage';
import { useAppDispatch } from 'src/store';
import useAppNavigation from 'src/shared/hooks/useAppNavigation';
import { ProjectViews } from 'src/routes/routesConfig';
import Tabs from 'src/shared/components/Tabs/Tabs';
import useProjectDefinition from './hooks/useProjectDefinition';
import styles from './NewProjectDefinition.module.scss';
import ReviewAndSubmit from './components/review-and-submit/ReviewAndSubmit';
import SamplingCadence from './components/sampling-cadence/SamplingCadence';
import ProjectAims from './components/project-aims/ProjectAims';
import ProjectDetails from './components/project-details/ProjectDetails';
import HabitatInsights from './components/habitat-insights/HabitatInsights';
import { getSectionStatus } from './helpers/sectionCompletion';

export const NewProjectDefinition = () => {
    const dispatch = useAppDispatch();
    const { currentCustomerId } = useCustomer();
    const navigate = useAppNavigation();
    const { saveProjectDefinitionForm, isEditMode, formState, onFormChange } = useProjectDefinition();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    // Extract projectId from URL when on edit route
    useEffect(() => {
        const isEditRouteCheck = location.pathname.includes('/edit-project-definition');
        const projectIdFromUrl = searchParams.get('projectId');

        if (isEditRouteCheck && projectIdFromUrl && projectIdFromUrl !== formState.projectId) {
            onFormChange({ projectId: projectIdFromUrl });
        }
    }, [location.pathname, searchParams, formState.projectId, onFormChange]);

    useEffect(() => {
        if (!currentCustomerId) {
            navigate.toProjects();
        }
    }, [currentCustomerId, navigate]);

    // Reset form state when component unmounts
    useEffect(() => {
        return () => {
            dispatch(reset());
        };
    }, [dispatch]);

    // Update URL to edit-project-definition with projectId when in edit mode
    useEffect(() => {
        if (isEditMode && formState.projectId) {
            const currentPath = location.pathname;
            const isEditRouteCheck = currentPath.includes('/edit-project-definition');

            if (!isEditRouteCheck) {
                const newPath = navigate.getPath(ProjectViews.EDIT_PROJECT_DEFINITION, {
                    projectId: formState.projectId,
                });
                window.history.replaceState({}, '', newPath);
            } else {
                // Ensure projectId is in URL params
                const projectIdParam = searchParams.get('projectId');
                if (projectIdParam !== formState.projectId) {
                    searchParams.set('projectId', formState.projectId);
                    setSearchParams(searchParams);
                }
            }
        }
    }, [isEditMode, formState.projectId, currentCustomerId, location.pathname, navigate, searchParams, setSearchParams]);

    // Calculate section statuses
    const sectionStatuses = useMemo(() => {
        const sections = ['projectDetails', 'projectAims', 'samplingCadence', 'habitatInsights'];
        return sections.map(section => getSectionStatus(formState, section));
    }, [formState]);

    const sectionRefs = {
        projectDetails: useRef<HTMLDivElement>(null),
        projectAims: useRef<HTMLDivElement>(null),
        samplingCadence: useRef<HTMLDivElement>(null),
        habitatInsights: useRef<HTMLDivElement>(null),
        review: useRef<HTMLDivElement>(null),
    };

    const containerRef = useRef<HTMLDivElement>(null);
    const [activeTab, setActiveTab] = useState<string>('projectDetails');

    const scrollToSection = (section: string) => {
        const ref = sectionRefs[section as keyof typeof sectionRefs];
        if (ref?.current) {
            ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            setActiveTab(section);
        }
    };

    // Build tabs based on visible sections
    const tabs = useMemo(() => {
        const sectionNames = ['projectDetails', 'projectAims', 'samplingCadence', 'habitatInsights', 'review'];
        const labels = ['Project Details', 'Project Aims', 'Sampling Cadence', 'Habitat Insights', 'Review'];

        return sectionNames
            .map((sectionName, index) => {
                if (index < sectionStatuses.length && !sectionStatuses[index].isVisible) {
                    return null;
                }
                return {
                    id: sectionName,
                    label: labels[index],
                };
            })
            .filter(Boolean) as Array<{ id: string; label: string }>;
    }, [sectionStatuses]);

    // Update active tab when features change - ensure it's still visible
    useEffect(() => {
        const visibleTabIds = tabs.map(tab => tab.id);

        // If current active tab is not visible, switch to first visible tab
        if (!visibleTabIds.includes(activeTab)) {
            const firstVisibleTab = visibleTabIds[0] || 'projectDetails';
            setActiveTab(firstVisibleTab);

            // Optionally scroll to the new active tab
            const ref = sectionRefs[firstVisibleTab as keyof typeof sectionRefs];
            if (ref?.current) {
                ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
    }, [tabs, activeTab]);

    // Update active tab based on scroll position - only consider visible sections
    useEffect(() => {
        if (tabs.length === 0 || !containerRef.current) return;

        let ticking = false;
        const projectDefinitionContainer = containerRef.current;

        const handleScroll = () => {
            if (!ticking) {
                window.requestAnimationFrame(() => {
                    const visibleSectionNames = tabs.map(tab => tab.id);
                    const stickyHeaderOffset = 200; // Height of sticky header + padding
                    const scrollTop = projectDefinitionContainer.scrollTop;
                    const scrollPosition = scrollTop + stickyHeaderOffset;

                    let activeSection = visibleSectionNames[0]; // Default to first section
                    let closestDistance = Infinity;

                    // Find the section whose top is closest to (but above) the sticky header offset
                    for (let i = 0; i < visibleSectionNames.length; i++) {
                        const section = visibleSectionNames[i];
                        const ref = sectionRefs[section as keyof typeof sectionRefs];
                        if (ref?.current) {
                            // Get position relative to the scroll container
                            const containerRect = projectDefinitionContainer.getBoundingClientRect();
                            const sectionRect = ref.current.getBoundingClientRect();
                            const sectionTop = sectionRect.top - containerRect.top + scrollTop;

                            // If we've scrolled past this section's top
                            if (scrollPosition >= sectionTop) {
                                const distance = scrollPosition - sectionTop;
                                // Choose the section with the smallest distance (most recently passed)
                                if (distance < closestDistance) {
                                    closestDistance = distance;
                                    activeSection = section;
                                }
                            }
                        }
                    }

                    setActiveTab(activeSection);
                    ticking = false;
                });
                ticking = true;
            }
        };

        // Set initial active tab on mount
        const timer = setTimeout(() => {
            handleScroll();
        }, 100);

        projectDefinitionContainer.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            clearTimeout(timer);
            projectDefinitionContainer.removeEventListener('scroll', handleScroll);
        };
    }, [tabs]);

    const mainContent = (
        <div ref={containerRef} className={styles.projectDefinition}>
            <div className={styles.progressSection}>
                <Tabs
                    tabs={tabs}
                    selectedTab={activeTab}
                    onTabChange={scrollToSection}
                    classNames={{
                        tabsContainer: styles.tabsContainer,
                        default: styles.tab,
                        selected: styles.tabSelected,
                    }}
                />
            </div>
            <div ref={sectionRefs.projectDetails}>
                <ProjectDetails />
            </div>
            {sectionStatuses[1]?.isVisible && (
                <div ref={sectionRefs.projectAims}>
                    <ProjectAims />
                </div>
            )}
            {sectionStatuses[2]?.isVisible && (
                <div ref={sectionRefs.samplingCadence}>
                    <SamplingCadence />
                </div>
            )}
            {sectionStatuses[3]?.isVisible && (
                <div ref={sectionRefs.habitatInsights}>
                    <HabitatInsights />
                </div>
            )}
            <div ref={sectionRefs.review}>
                <ReviewAndSubmit />
            </div>
        </div>
    );

    const onSave = async () => {
        try {
            await saveProjectDefinitionForm();
        } catch (error: any) {
            console.error('Error saving project definition:', error);
        }
    };

    const saveMessage = (
        <WarningMessage
            message={
                <>
                    This project definition is NOT submitted.
                    <br />
                    <br />
                    It will be retained with ‘incomplete’ status for 5 days, after which it will be deleted.
                    <br />
                    <br />
                    This incomplete project definition will only be editable by you.
                </>
            }
        />
    );

    // Check if we're on edit route from URL as well, since isEditMode might not be set immediately
    const isEditRoute = location.pathname.includes('/edit-project-definition');
    const pageTitle = isEditMode || isEditRoute ? 'Edit project definition' : 'New project definition';

    // Show "Save and exit" only in create mode (not in edit mode or edit route) and when a project is selected
    const otherActions = !isEditMode && !isEditRoute && formState.projectId && (
        <div className={styles.otherActions}>
            <Confirm
                onConfirm={onSave}
                title='Are you sure you want to save and exit from this project definition?'
                content={saveMessage}
                width={'500px'}
            >
                <SecondaryButton>
                    <SaveOutlined />
                    Save and exit
                </SecondaryButton>
            </Confirm>
        </div>
    );

    return <StandardAppLayout mainContent={mainContent} title={pageTitle} otherActions={otherActions} />;
};

export default NewProjectDefinition;
